import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { employees, VALUE_KIND_LABELS } from '@/api/hr';

/**
 * Read-only блок «Планові нарахування» на картці працівника (як у 1С).
 *
 * Два режими (перемикач):
 *  - «Активні» — постійні нарахування, що діють на сьогодні (оклад/надбавки/
 *    доплати/премії), які автоматично підтягуються у розрахунок зарплати.
 *  - «Історія» — ЛИШЕ непроведені (чернетки наказів) + недіючі (закриті) записи.
 *    Діючі-проведені показуються у вкладці «Активні», не дублюються в історії.
 *
 * Ведуться документами (наказ прийому/переміщення, «Введення планових») — тут
 * лише перегляд.
 */

interface Props {
  employeeId: string;
}

const fmtDate = (v: string | null) =>
  v ? new Date(v).toLocaleDateString('uk-UA') : '—';

const fmtValue = (kind: string, value: string): string => {
  const n = Number(value);
  if (kind === 'PERCENT') return `${n}%`;
  if (kind === 'RATE') return `${n} (тариф)`;
  return `${n.toLocaleString('uk-UA', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ₴`;
};

const docLabel = (t: string): string => {
  if (t.includes('hire_orders')) return 'Прийом';
  if (t.includes('staff_transfers')) return 'Переміщення';
  if (t.includes('dismissal_orders')) return 'Звільнення';
  if (t.includes('planned_accrual_orders')) return 'Наказ про зміну нарахувань';
  return t.replace('doc.', '');
};

// Маршрут до документа-підстави по його типу + id (для клікабельного посилання).
// Лише для типів зі своєю сторінкою-редактором; інакше null (звичайний текст).
const docRoute = (t: string, id: string): string | null => {
  if (t.includes('hire_orders')) return `/hire-orders/${id}`;
  if (t.includes('staff_transfers')) return `/staff-transfers/${id}`;
  if (t.includes('dismissal_orders')) return `/dismissal-orders/${id}`;
  if (t.includes('planned_accrual_orders')) return `/payroll/planned-accrual-orders/${id}`;
  return null;
};

const tabBtn = (active: boolean) => ({
  padding: '0.25rem 0.7rem',
  fontSize: '0.8rem',
  cursor: 'pointer',
  border: '1px solid #cbd5e1',
  background: active ? '#2563eb' : '#fff',
  color: active ? '#fff' : '#334155',
} as const);

export default function EmployeePlannedAccrualsSection({ employeeId }: Props) {
  const navigate = useNavigate();
  const [history, setHistory] = useState(false);

  const q = useQuery({
    queryKey: ['employee-planned-accruals', employeeId, history],
    queryFn: () => employees.plannedAccruals(employeeId, history),
    refetchOnMount: true,
  });

  const rows = q.data ?? [];

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '1rem' }}>
        <h3 style={{ margin: 0 }}>Планові нарахування</h3>
        <div style={{ display: 'flex', borderRadius: 4, overflow: 'hidden' }}>
          <button type="button" style={{ ...tabBtn(!history), borderRadius: '4px 0 0 4px' }}
                  onClick={() => setHistory(false)}>
            Активні
          </button>
          <button type="button" style={{ ...tabBtn(history), borderRadius: '0 4px 4px 0', borderLeft: 'none' }}
                  onClick={() => setHistory(true)}>
            Історія
          </button>
        </div>
      </div>
      <p style={{ color: '#64748b', fontSize: '0.8rem', margin: '0.3rem 0 0.4rem' }}>
        {history
          ? 'Закриті (недіючі) нарахування + рядки з непроведених наказів. Діючі — у вкладці «Активні».'
          : 'Постійні нарахування, що діють зараз і автоматично підтягуються у розрахунок зарплати.'}
        {rows.some((r) => r.is_draft) && (
          <span style={{ color: '#d97706' }}>
            {' '}Рядки «чернетка» — з непроведених наказів; потраплять у розрахунок після проведення наказу.
          </span>
        )}
      </p>
      {q.isLoading ? (
        <p style={{ color: '#94a3b8' }}>Завантаження…</p>
      ) : rows.length === 0 ? (
        <p style={{ color: '#64748b' }}>
          {history
            ? 'Немає історії планових нарахувань.'
            : 'Немає активних планових нарахувань. Оклад береться з кадрового стану; '
              + 'надбавки/доплати додаються наказом або документом «Введення планових».'}
        </p>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
          <thead>
            <tr style={{ background: '#f8fafc', textAlign: 'left' }}>
              <th style={{ padding: '0.4rem' }}>Вид нарахування</th>
              <th style={{ padding: '0.4rem', textAlign: 'right' }}>Значення</th>
              <th style={{ padding: '0.4rem' }}>Спосіб</th>
              <th style={{ padding: '0.4rem' }}>Діє з</th>
              <th style={{ padding: '0.4rem' }}>Діє по</th>
              <th style={{ padding: '0.4rem' }}>Підстава</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const closed = history && r.valid_to != null;
              return (
                <tr key={`${r.is_draft ? 'd' : 'r'}-${r.id}`}
                    style={{
                      borderTop: '1px solid #e2e8f0',
                      color: closed ? '#94a3b8' : undefined,
                      background: r.is_draft ? '#fffbeb' : undefined,
                    }}>
                  <td style={{ padding: '0.4rem' }}>{r.type_name}</td>
                  <td style={{ padding: '0.4rem', textAlign: 'right', fontWeight: 600 }}>
                    {fmtValue(r.value_kind, r.value)}
                  </td>
                  <td style={{ padding: '0.4rem', color: '#64748b' }}>
                    {VALUE_KIND_LABELS[r.value_kind] ?? r.value_kind}
                  </td>
                  <td style={{ padding: '0.4rem' }}>{fmtDate(r.valid_from)}</td>
                  <td style={{ padding: '0.4rem' }}>
                    {r.is_draft
                      ? <span style={{ color: '#d97706' }}>чернетка</span>
                      : r.valid_to == null
                        ? <span style={{ color: '#16a34a' }}>діє</span>
                        : fmtDate(r.valid_to)}
                  </td>
                  <td style={{ padding: '0.4rem', color: '#64748b' }}>
                    {(() => {
                      const label = docLabel(r.source_doc_type)
                        + (r.doc_number ? ` №${r.doc_number}` : '');
                      const route = r.source_doc_id
                        ? docRoute(r.source_doc_type, r.source_doc_id) : null;
                      return route
                        ? <a title="Відкрити документ-підставу"
                             style={{ cursor: 'pointer', color: 'var(--primary)' }}
                             onClick={() => navigate(route)}>{label}</a>
                        : label;
                    })()}
                    {r.is_draft && (
                      <span title="З непроведеного наказу — потрапить у розрахунок після проведення"
                            style={{ marginLeft: 4, color: '#d97706' }}>⚠</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </>
  );
}
